import { mqttService } from '../../services/mqttService';
import { AmplifierData, AmplifierControlData, AmplifierProjectData, ProgramStatusMessage, HamlibControlRequestMessage, ProgramStatusResponseMessage, HamlibControlResponseMessage, UpdateAlarmLogRequestMessage } from './amplifier';



/**
 * AmplifierControl MQTT服务
 * 专门用于功放控制组件的MQTT通信
 */
class AmplifierMqttService {
    private paramCommandIntervalId: NodeJS.Timeout | null = null;

    /**
     * 启动参数命令定时任务（已禁用）
     * 前端不再定时发送 synCommand，仅保留接口以兼容现有调用；如有需要由后端或其他方式下发
     */
    startParamCommandInterval(_deviceId: string, _userId: number): void {
        this.stopParamCommandInterval();
    }
    /**
     * 发送json_command指令
     * amplifier/device/{deviceId}/json_command
     * 参数分别是HamlibControlResponseMessage和ProgramStatusResponseMessage
     */
    async sendJsonCommand(deviceId: string, message: HamlibControlResponseMessage | ProgramStatusResponseMessage | UpdateAlarmLogRequestMessage): Promise<void> {
        try {
            // 确保MQTT服务已连接
            if (!mqttService.isConnected()) {
                console.error('AmplifierMqttService: MQTT服务未连接，无法发送json_command指令');
                throw new Error('MQTT服务未连接');
            }

            // 检查服务是否已初始化，确保MQTT管理器已创建
            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法发送json_command指令');
                throw new Error('MQTT服务未初始化');
            }

            const mqttManager = mqttService.getMqttManager();

            // 主题格式：amplifier/device/{deviceId}/json_command
            const topic = `amplifier/device/${deviceId}/json_command`;
            await mqttManager.publishJson(topic, message, 1, false);
            console.log('AmplifierMqttService: json_command指令发送成功:', message);
        } catch (error) {
            console.error('AmplifierMqttService: json_command指令发送失败:', error);
            throw error;
        }
    }


    /**
     * 发送功放设置指令
     * 功放设置指令格式：{"action":"updateAmplifierData","data":{amplifierData}}
     * 其中amplifierData包含triggerMode、tuneMode、power、frequency、inputAttenuator、inputAntenna、outputAntenna、alarmWord等参数
     * 其中tuneMode为0表示自动调频，1表示手动调频
     * 其中power为0表示关闭，1表示开启
     * 其中inputAttenuator为0表示关闭，1表示开启
     * 当用户在页面上操作按钮修改了amplifierData后，调用此方法发送功放设置指令
     */
    async sendAmpConfigCommand(deviceId: string, controlData: AmplifierControlData): Promise<void> {
        try {
            // 确保MQTT服务已连接
            if (!mqttService.isConnected()) {
                console.error('AmplifierMqttService: MQTT服务未连接，无法发送放大器数据更新');
                throw new Error('MQTT服务未连接');
            }

            // 检查服务是否已初始化，确保MQTT管理器已创建
            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法发送放大器数据更新');
                throw new Error('MQTT服务未初始化');
            }

            const mqttManager = mqttService.getMqttManager();

            // 构建消息对象，包含控制数据
            const message = {
                action: 'ampConfigCommand',
                ...controlData
            };

            // 主题格式：server/device/{deviceId}/command
            const topic = `server/device/${deviceId}/command`;
            await mqttManager.publishJson(topic, message, 1, false);
            console.log('AmplifierMqttService: 功放设置指令发送成功:', message);
        } catch (error) {
            console.error('AmplifierMqttService: 功放设置指令发送失败:', error);
            throw error;
        }
    }
    /**
     *发送工程参数指令
     *
     */
    async sendProjectParametersCommand(deviceId: string, projectData: AmplifierProjectData): Promise<void> {
        try {
            // 确保MQTT服务已连接
            if (!mqttService.isConnected()) {
                console.error('AmplifierMqttService: MQTT服务未连接，无法发送工程参数指令');
                throw new Error('MQTT服务未连接');
            }

            // 检查服务是否已初始化，确保MQTT管理器已创建
            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法发送工程参数指令');
                throw new Error('MQTT服务未初始化');
            }

            const message = {
                command: 'projectParameters',
                ...projectData
            };
            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/command`;
            await mqttManager.publishJson(topic, message, 1, false);
            console.log('AmplifierMqttService: 工程参数指令发送成功:', message);
        } catch (error) {
            console.error('AmplifierMqttService: 工程参数指令发送失败:', error);
            throw error;
        }
    }


    /**
     * 停止参数命令定时任务
     */
    stopParamCommandInterval(): void {
        if (this.paramCommandIntervalId) {
            clearInterval(this.paramCommandIntervalId);
            this.paramCommandIntervalId = null;
            console.log('AmplifierMqttService: 参数命令定时任务已停止');
        }
    }


    /** 状态回调节流间隔（ms），降低 setState 频率以提升渲染效率，建议 80～150 */
    private static readonly STATUS_THROTTLE_MS = 100;
    /** 节流用：待下发的状态与定时器句柄（按 deviceId 隔离，此处单设备可复用） */
    private pendingAmplifierData: AmplifierData | null = null;
    private pendingProgramStatus: ProgramStatusMessage | null = null;
    private pendingHamlib: HamlibControlRequestMessage | null = null;
    private throttleTimerId: ReturnType<typeof setTimeout> | null = null;

    /**
     * 订阅设备状态主题
     * 订阅 server/device/{deviceId}/status 主题并处理接收到的消息
     * 接收效率：热路径仅做一次解析、按 topic 分支；生产环境减少 log。
     * 渲染效率：对回调做节流，合并短时间内的多次消息为一次下发，减少 React 重绘。
     */
    async subscribeToDeviceStatus(deviceId: string, callback: (data: AmplifierData) => void, programStatusCallback?: (data: ProgramStatusMessage) => void, hamlibControlCallback?: (data: HamlibControlRequestMessage) => void): Promise<void> {
        try {
            if (!mqttService.isConnected()) {
                try {
                    await mqttService.connect();
                } catch (connectError) {
                    console.error('AmplifierMqttService: 连接MQTT服务失败:', connectError);
                    return;
                }
            }

            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法订阅设备状态主题');
                return;
            }

            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/status`;
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            const isDev = process.env.NODE_ENV === 'development';

            await mqttManager.subscribe(topic, 1);
            await mqttManager.subscribe(jsonStatusTopic, 1);
            if (isDev) {
                console.log('AmplifierMqttService: 已订阅设备状态主题:', topic, jsonStatusTopic);
            }

            const self = this;
            const flushThrottled = () => {
                self.throttleTimerId = null;
                if (self.pendingAmplifierData !== null) {
                    const data = self.pendingAmplifierData;
                    self.pendingAmplifierData = null;
                    callback(data);
                }
                if (self.pendingProgramStatus !== null && programStatusCallback) {
                    const msg = self.pendingProgramStatus;
                    self.pendingProgramStatus = null;
                    programStatusCallback(msg);
                }
                if (self.pendingHamlib !== null && hamlibControlCallback) {
                    const msg = self.pendingHamlib;
                    self.pendingHamlib = null;
                    hamlibControlCallback(msg);
                }
            };

            const scheduleFlush = () => {
                if (self.throttleTimerId === null) {
                    self.throttleTimerId = setTimeout(flushThrottled, AmplifierMqttService.STATUS_THROTTLE_MS);
                }
            };

            mqttManager.setCallbacks({
                onMessage: (receivedTopic: string, message: any) => {
                    if (receivedTopic === topic) {
                        try {
                            const messageString = typeof message === 'string' ? message : message.toString();
                            const data = JSON.parse(messageString) as AmplifierData;
                            if (isDev) console.log('AmplifierMqttService: 接收到设备状态消息');
                            self.pendingAmplifierData = data;
                            scheduleFlush();
                        } catch (error) {
                            console.error('AmplifierMqttService: 解析设备状态消息失败:', error);
                        }
                        return;
                    }
                    if (receivedTopic === jsonStatusTopic) {
                        try {
                            const messageString = typeof message === 'string' ? message : message.toString();
                            const jsonMessage = JSON.parse(messageString);
                            if (jsonMessage.cmd === 'update_program_status') {
                                self.pendingProgramStatus = jsonMessage as ProgramStatusMessage;
                                if (isDev) console.log('AmplifierMqttService: 接收到程序状态更新消息');
                                scheduleFlush();
                            } else if (jsonMessage.cmd === 'set_hamlib') {
                                self.pendingHamlib = jsonMessage as HamlibControlRequestMessage;
                                if (isDev) console.log('AmplifierMqttService: 接收到Hamlib控制消息');
                                scheduleFlush();
                            }
                        } catch (error) {
                            console.error('AmplifierMqttService: 解析JSON状态消息失败:', error);
                        }
                        return;
                    }
                    if (isDev) console.warn('AmplifierMqttService: 未处理主题:', receivedTopic);
                }
            });
        } catch (error) {
            console.error('AmplifierMqttService: 订阅设备状态主题失败:', error);
        }
    }

    /** 清除节流定时器与待下发数据，避免卸载后仍触发回调 */
    private clearThrottle(): void {
        if (this.throttleTimerId !== null) {
            clearTimeout(this.throttleTimerId);
            this.throttleTimerId = null;
        }
        this.pendingAmplifierData = null;
        this.pendingProgramStatus = null;
        this.pendingHamlib = null;
    }

    /**
     * 取消订阅设备状态主题
     */
    async unsubscribeFromDeviceStatus(deviceId: string): Promise<void> {
        this.clearThrottle();
        try {
            // 确保MQTT服务已连接
            if (!mqttService.isConnected()) {
                try {
                    await mqttService.connect();
                } catch (connectError) {
                    console.error('AmplifierMqttService: 连接MQTT服务失败:', connectError);
                    return; // 连接失败时直接返回，不抛出错误
                }
            }

            // 检查服务是否已初始化，确保MQTT管理器已创建
            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法取消订阅设备状态主题');
                return; // 服务未初始化时直接返回，不抛出错误
            }

            const mqttManager = mqttService.getMqttManager();

            // 取消订阅主题
            const topic = `server/device/${deviceId}/status`;
            await mqttManager.unsubscribe(topic);
            console.log('AmplifierMqttService: 已取消订阅设备状态主题:', topic);

            // 取消订阅json_status主题
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            await mqttManager.unsubscribe(jsonStatusTopic);
            console.log('AmplifierMqttService: 已取消订阅JSON状态主题:', jsonStatusTopic);
        } catch (error) {
            console.error('AmplifierMqttService: 取消订阅设备状态主题失败:', error);
            // 不抛出错误，避免影响组件渲染
        }
    }

    /**
     * 销毁服务
     */
    dispose(): void {
        this.stopParamCommandInterval();
        this.clearThrottle();
    }
}

// 创建服务实例
const amplifierMqttService = new AmplifierMqttService();

// 导出服务实例
export { amplifierMqttService };
